import React, { useEffect, useState } from "react";
import axios from "axios";

const NewsInsights = () => {
  const [news, setNews] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [form, setForm] = useState({
    title: "",
    paragraph: "",
    videoLink: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const API_URL = `${process.env.REACT_APP_API_URL}/api/newsinsights`;

  // 📥 FETCH
  const fetchNews = async () => {
    try {
      const res = await axios.get(API_URL);
      setNews(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  // 🧠 OPEN MODAL
  const openModal = (item = null) => {
    if (item) {
      setForm({
        title: item.title,
        paragraph: item.paragraph,
        videoLink: item.videoLink,
      });
      setPreview(item.image);
      setEditingId(item._id);
    } else {
      setForm({ title: "", paragraph: "", videoLink: "" });
      setPreview(null);
      setImageFile(null);
      setEditingId(null);
    }
    setIsOpen(true);
  };

  const closeModal = () => setIsOpen(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  // 🚀 CREATE / UPDATE
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("paragraph", form.paragraph);
      formData.append("videoLink", form.videoLink);

      if (imageFile) formData.append("image", imageFile);

      if (editingId) {
        await axios.put(`${API_URL}/update/${editingId}`, formData);
      } else {
        await axios.post(`${API_URL}/create`, formData);
      }

      await fetchNews();
      setIsOpen(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // 🗑️ DELETE
  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleteLoadingId(selectedId);

    try {
      await axios.delete(`${API_URL}/delete/${selectedId}`);
      await fetchNews();
    } catch (err) {
      console.log(err);
    } finally {
      setDeleteLoadingId(null);
      setShowDeleteModal(false);
      setSelectedId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">News Insights</h1>

        <button
          onClick={() => openModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg"
        >
          + Create
        </button>
      </div>

      {/* CARDS */}
      <div className="grid md:grid-cols-3 gap-6">

        {news.map((item) => (
          <div
            key={item._id}
            className="bg-white rounded-xl shadow flex flex-col h-[450px] overflow-hidden"
          >

            {/* IMAGE */}
            <img
              src={item.image}
              alt=""
              className="h-44 w-full object-cover"
            />

            {/* CONTENT */}
            <div className="p-4 flex flex-col flex-1">

              <h2 className="font-semibold text-lg line-clamp-1">
                {item.title}
              </h2>

              {/* SCROLL TEXT */}
              <div className="flex-1 overflow-y-auto mt-2 pr-2">
                <p className="text-sm text-gray-600">
                  {item.paragraph}
                </p>
              </div>

              {/* ACTIONS */}
              <div className="flex justify-between mt-4 pt-3 border-t">

                <button
                  onClick={() => openModal(item)}
                  className="bg-yellow-500 text-white px-3 py-1 rounded"
                >
                  Edit
                </button>

                <button
                  onClick={() => handleDeleteClick(item._id)}
                  className="bg-red-600 text-white px-3 py-1 rounded"
                >
                  Delete
                </button>

              </div>
            </div>
          </div>
        ))}

      </div>

      {/* ✨ CREATE / UPDATE MODAL */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl p-6">

            <h2 className="text-xl font-semibold mb-4">
              {editingId ? "Update News" : "Create News"}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">

              <input
                type="text"
                name="title"
                placeholder="Enter title..."
                value={form.title}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg"
                required
              />

              <textarea
                name="paragraph"
                placeholder="Enter description..."
                value={form.paragraph}
                onChange={handleChange}
                rows={4}
                className="w-full p-3 border rounded-lg resize-none"
                required
              />

              {/* IMAGE INPUT */}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="w-full"
              />

              {/* IMAGE PREVIEW FIXED */}
              {preview && (
                <div className="w-full h-32 flex items-center justify-center border rounded-lg overflow-hidden bg-gray-50">
                  <img
                    src={preview}
                    alt="preview"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              )}

              <input
                type="text"
                name="videoLink"
                placeholder="Video link (optional)"
                value={form.videoLink}
                onChange={handleChange}
                className="w-full p-3 border rounded-lg"
              />

              <div className="flex justify-end gap-3">

                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 bg-gray-200 rounded"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className={`px-4 py-2 text-white rounded
                    ${loading ? "bg-blue-300" : "bg-blue-600"}`}
                >
                  {loading ? "Saving..." : editingId ? "Update" : "Create"}
                </button>

              </div>

            </form>
          </div>
        </div>
      )}

      {/* 🗑️ DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">

          <div className="bg-white w-[380px] rounded-xl p-6">

            <h2 className="text-lg font-semibold">
              Confirm Delete
            </h2>

            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to delete this item?
            </p>

            <div className="flex justify-end gap-3 mt-5">

              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancel
              </button>

              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                {deleteLoadingId ? "Deleting..." : "Confirm"}
              </button>

            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default NewsInsights;